// /clients/:id - detail view for one attributed tenant.
// Phase 6.1 Chunk f.

import { useCallback, useEffect, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import { PortalLayout } from '../components/PortalLayout';
import {
  Card,
  CardLabel,
  CardValue,
  Button,
  colors,
  headingStyle,
  pageWrapperStyle,
  mutedStyle,
  linkStyle,
} from '../components/ui';
import { useLang, fmt } from '../i18n';

interface ConsentEntry {
  id: string;
  scope: string;
  granted: boolean;
  ts: string;
  withdrawn_at: string | null;
  policy_version: string;
}

interface ConsentResponse {
  has_active_consent: boolean;
  active_scopes: string[];
  history: ConsentEntry[];
}

interface ClientRow {
  id: string;
  tenant_id: string;
  closed_at: string;
  initial_tier: string;
  initial_mrr_cad: number | null;
  current_mrr_cad: number | null;
  status: string;
  customer_csat_score: number | null;
  customer_consent_proof_url: string | null;
}

export default function ClientDetail() {
  const { t } = useLang();
  const { id } = useParams<{ id: string }>();
  const [row, setRow] = useState<ClientRow | null>(null);
  const [loading, setLoading] = useState(true);
  const [notFound, setNotFound] = useState(false);
  const [consent, setConsent] = useState<ConsentResponse | null>(null);
  const [consentBusy, setConsentBusy] = useState(false);
  const [consentMsg, setConsentMsg] = useState<string | null>(null);

  const loadConsent = useCallback(async (tenantId: string) => {
    try {
      const res = await fetch(`/api/partner/consent?tenant_id=${encodeURIComponent(tenantId)}`);
      if (res.ok) {
        const json = (await res.json()) as ConsentResponse;
        setConsent(json);
      }
    } catch {
      /* ignore; consent UI degrades gracefully */
    }
  }, []);

  async function captureConsent() {
    if (!id) return;
    setConsentBusy(true);
    setConsentMsg(null);
    try {
      const res = await fetch('/api/partner/consent', {
        method: 'POST',
        headers: { 'content-type': 'application/json' },
        body: JSON.stringify({ tenant_id: id, scope: 'partner_views' }),
      });
      if (res.ok) {
        setConsentMsg(t.clientDetail.consentCaptured);
        await loadConsent(id);
      } else {
        const j = await res.json().catch(() => ({}));
        setConsentMsg(`${t.common.error}: ${(j as { error?: string }).error ?? res.status}`);
      }
    } catch {
      setConsentMsg(t.common.networkError);
    } finally {
      setConsentBusy(false);
    }
  }

  async function withdrawConsent(consentId: string) {
    if (!id) return;
    setConsentBusy(true);
    setConsentMsg(null);
    try {
      const res = await fetch(`/api/partner/consent?id=${encodeURIComponent(consentId)}`, {
        method: 'DELETE',
      });
      if (res.ok) {
        setConsentMsg(t.clientDetail.consentWithdrawnSuccess);
        await loadConsent(id);
      } else {
        const j = await res.json().catch(() => ({}));
        setConsentMsg(`${t.common.error}: ${(j as { error?: string }).error ?? res.status}`);
      }
    } catch {
      setConsentMsg(t.common.networkError);
    } finally {
      setConsentBusy(false);
    }
  }

  useEffect(() => {
    if (!id) return;
    let cancelled = false;
    (async () => {
      try {
        // Same /api/partner/clients endpoint, then filter locally by tenant_id.
        // RLS on Supabase guarantees we only ever get our own org's rows.
        // Per ADR-0022 T11 mitigation: a tenant we cannot see returns
        // empty list (indistinguishable from "doesn't exist").
        const res = await fetch('/api/partner/clients');
        if (!res.ok) {
          if (!cancelled) {
            setNotFound(true);
            setLoading(false);
          }
          return;
        }
        const json = (await res.json()) as { rows: ClientRow[] };
        const match = json.rows.find((r) => r.tenant_id === id);
        if (!cancelled) {
          if (match) {
            setRow(match);
            loadConsent(id);
          } else {
            setNotFound(true);
          }
          setLoading(false);
        }
      } catch {
        if (!cancelled) {
          setNotFound(true);
          setLoading(false);
        }
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [id, loadConsent]);

  return (
    <PortalLayout>
      <div style={pageWrapperStyle}>
        <p style={{ marginBottom: '16px' }}>
          <Link to="/clients" style={{ ...linkStyle, fontSize: '0.85rem' }}>
            &larr; {t.clientDetail.backLink}
          </Link>
        </p>

        {loading && <div style={mutedStyle}>{t.common.loading}</div>}

        {notFound && !loading && (
          <Card>
            <h1 style={{ fontSize: '1.5rem', fontWeight: 600, margin: '0 0 12px' }}>
              {t.clientDetail.notFoundTitle}
            </h1>
            <p style={mutedStyle}>
              {t.clientDetail.notFoundMsg}
            </p>
          </Card>
        )}

        {row && (
          <>
            <h1 style={headingStyle}>{t.clientDetail.title}</h1>
            <p style={{ ...mutedStyle, fontFamily: 'monospace', fontSize: '0.75rem', marginBottom: '24px' }}>
              {row.tenant_id}
            </p>

            <div
              style={{
                display: 'grid',
                gridTemplateColumns: 'repeat(auto-fit, minmax(220px, 1fr))',
                gap: '16px',
              }}
            >
              <Card>
                <CardLabel>{t.clientDetail.closedOn}</CardLabel>
                <CardValue style={{ fontSize: '1rem' }}>
                  {new Date(row.closed_at).toLocaleDateString()}
                </CardValue>
              </Card>
              <Card>
                <CardLabel>{t.clientDetail.tierAtClose}</CardLabel>
                <CardValue style={{ fontSize: '1rem' }}>{row.initial_tier}</CardValue>
              </Card>
              <Card>
                <CardLabel>{t.clientDetail.initialMrr}</CardLabel>
                <CardValue style={{ fontSize: '1rem' }}>
                  {row.initial_mrr_cad != null ? `$${Number(row.initial_mrr_cad).toFixed(2)}` : "-"}
                </CardValue>
              </Card>
              <Card>
                <CardLabel>{t.clientDetail.currentMrr}</CardLabel>
                <CardValue style={{ fontSize: '1rem' }}>
                  {row.current_mrr_cad != null ? `$${Number(row.current_mrr_cad).toFixed(2)}` : "-"}
                </CardValue>
              </Card>
              <Card>
                <CardLabel>{t.clientDetail.status}</CardLabel>
                <CardValue style={{ fontSize: '1rem' }}>{row.status}</CardValue>
              </Card>
              <Card>
                <CardLabel>{t.clientDetail.csat}</CardLabel>
                <CardValue style={{ fontSize: '1rem' }}>
                  {row.customer_csat_score != null ? `${row.customer_csat_score}/5` : t.clientDetail.csatNone}
                </CardValue>
              </Card>
              <Card>
                <CardLabel>{t.clientDetail.loi25Legacy}</CardLabel>
                <CardValue style={{ fontSize: '1rem' }}>
                  {row.customer_consent_proof_url ? (
                    <span style={{ color: colors.success }}>{t.clients.loi25OnFile}</span>
                  ) : (
                    <span style={{ color: colors.danger }}>{t.clientDetail.loi25Missing}</span>
                  )}
                </CardValue>
              </Card>
            </div>

            {/* Live consent ledger (partner_data_consent table) */}
            <Card style={{ marginTop: '24px' }}>
              <CardLabel>{t.clientDetail.consentLedgerTitle}</CardLabel>
              <p style={{ ...mutedStyle, fontSize: '0.8rem', marginBottom: '12px' }}>
                {t.clientDetail.consentLedgerDesc}
              </p>

              {consent === null ? (
                <div style={mutedStyle}>{t.clientDetail.consentLoading}</div>
              ) : (
                <>
                  <div style={{ marginBottom: '12px' }}>
                    {consent.has_active_consent ? (
                      <span style={{ color: colors.success, fontSize: '0.95rem' }}>
                        {fmt(t.clientDetail.consentActive, { scopes: consent.active_scopes.join(', ') })}
                      </span>
                    ) : (
                      <span style={{ color: colors.danger, fontSize: '0.95rem' }}>
                        {t.clientDetail.consentNone}
                      </span>
                    )}
                  </div>

                  {!consent.has_active_consent && (
                    <Button
                      variant="primary"
                      busy={consentBusy}
                      onClick={captureConsent}
                    >
                      {t.clientDetail.consentCapture}
                    </Button>
                  )}

                  {consent.history.length > 0 && (
                    <div style={{ marginTop: '16px' }}>
                      <div style={{ ...mutedStyle, fontSize: '0.75rem', marginBottom: '8px' }}>
                        {fmt(t.clientDetail.consentHistory, { count: consent.history.length })}
                      </div>
                      {consent.history.map((h) => (
                        <div
                          key={h.id}
                          style={{
                            padding: '8px 12px',
                            borderRadius: '8px',
                            border: `1px solid ${colors.border}`,
                            marginBottom: '6px',
                            fontSize: '0.8rem',
                            display: 'flex',
                            justifyContent: 'space-between',
                            alignItems: 'center',
                            gap: '12px',
                          }}
                        >
                          <div>
                            <div style={{ fontFamily: 'monospace', fontSize: '0.75rem' }}>{h.scope}</div>
                            <div style={{ ...mutedStyle, fontSize: '0.7rem' }}>
                              {new Date(h.ts).toLocaleString()}{' '}
                              {h.withdrawn_at
                                ? `(${fmt(t.clientDetail.consentWithdrawn, { date: new Date(h.withdrawn_at).toLocaleDateString() })})`
                                : ''}
                            </div>
                          </div>
                          {h.granted && !h.withdrawn_at && (
                            <Button
                              variant="danger"
                              busy={consentBusy}
                              onClick={() => withdrawConsent(h.id)}
                              style={{ padding: '4px 10px', fontSize: '0.75rem' }}
                            >
                              {t.clientDetail.consentWithdraw}
                            </Button>
                          )}
                        </div>
                      ))}
                    </div>
                  )}

                  {consentMsg && (
                    <div
                      style={{
                        marginTop: '12px',
                        fontSize: '0.85rem',
                        color: consentMsg.startsWith(t.common.error) ? colors.danger : colors.success,
                      }}
                    >
                      {consentMsg}
                    </div>
                  )}
                </>
              )}
            </Card>
          </>
        )}
      </div>
    </PortalLayout>
  );
}
